/*
 This module provides all project-related API interactions for the application.
 It handles:
  - Project management (creation, updates, deletion)
  - Project listing and detail retrieval
  - Setting active project
  - API communication with authentication
 All functions include error handling and proper typing for responses.
 The service uses axios for HTTP requests and maintains consistent
 authentication header handling.
 */

import axiosInstance, { extractErrorMessage } from './axiosConfig';
import {
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectResponse,
  ProjectsListResponse,
  ProjectDetailResponse,
  ProjectDocument,
} from '../types/project';

/*
 Create a new project
 Initializes a new project for the authenticated user

  @async
  @param {CreateProjectRequest} data - Project creation data
  @returns {Promise<ProjectResponse>} Created project data
  @throws {Error} If project creation fails

  @example
  ```typescript
  try {
    const newProject = await createProject({
      name: "My Project",
      description: "Project description",
      custom_instructions: "Custom instructions here"
    });
    console.log('Created project:', newProject.data);
  } catch (error) {
    console.error('Failed to create project:', error);
  }
  ```
 */
export const createProject = async (data: CreateProjectRequest): Promise<ProjectResponse> => {
  try {
    const response = await axiosInstance.post('/projects/', data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao criar projeto'
      )
    );
  }
};

/*
  Get all projects for the current user
  Retrieves a list of all projects associated with the authenticated user

  @async
  @param {number} [limit=20] - Maximum number of projects to return
  @param {number} [offset=0] - Number of projects to skip
  @returns {Promise<ProjectsListResponse>} List of user's projects with pagination metadata
  @throws {Error} If projects cannot be fetched

  @example
  ```typescript
  try {
    const projects = await getUserProjects(50, 0);
    console.log('User projects:', projects.data);
    console.log('Has more:', projects.metadata?.has_more);
  } catch (error) {
    console.error('Failed to fetch projects:', error);
  }
  ```
 */
export const getUserProjects = async (
  limit: number = 20,
  offset: number = 0
): Promise<ProjectsListResponse> => {
  try {
    const response = await axiosInstance.get('/projects/', {
      params: { limit, offset }
    });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar projetos'
      )
    );
  }
};

/*
  Get details of a specific project
  Retrieves complete information for a single project

  @async
  @param {string} projectId - UUID of the project to fetch
  @returns {Promise<ProjectResponse>} Project details
  @throws {Error} If project cannot be fetched

  @example
  ```typescript
  try {
    const project = await getProject("550e8400-e29b-41d4-a716-446655440000");
    console.log('Project details:', project.data);
  } catch (error) {
    console.error('Failed to fetch project:', error);
  }
  ```
 */
export const getProject = async (projectId: string): Promise<ProjectResponse> => {
  try {
    const response = await axiosInstance.get(`/projects/${projectId}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar projeto'
      )
    );
  }
};

/*
  Get detailed information of a specific project including its chats
  Retrieves complete information for a single project with all associated chats

  @async
  @param {string} projectId - UUID of the project to fetch
  @returns {Promise<ProjectDetailResponse>} Project details with chats
  @throws {Error} If project detail cannot be fetched

  @example
  ```typescript
  try {
    const projectDetail = await getProjectDetail("550e8400-e29b-41d4-a716-446655440000");
    console.log('Project chats:', projectDetail.data.chats);
  } catch (error) {
    console.error('Failed to fetch project detail:', error);
  }
  ```
 */
export const getProjectDetail = async (projectId: string): Promise<ProjectDetailResponse> => {
  try {
    const response = await axiosInstance.get(`/projects/${projectId}/detail`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar detalhes do projeto'
      )
    );
  }
};

/*
  Update an existing project
  Modifies project information

  @async
  @param {string} projectId - UUID of the project to update
  @param {UpdateProjectRequest} data - Updated project data
  @returns {Promise<ProjectResponse>} Updated project data
  @throws {Error} If update fails

  @example
  ```typescript
  try {
    const updatedProject = await updateProject("550e8400-e29b-41d4-a716-446655440000", {
      name: "Updated Project Name",
      description: "New description"
    });
    console.log('Updated project:', updatedProject.data);
  } catch (error) {
    console.error('Failed to update project:', error);
  }
  ```
 */
export const updateProject = async (
  projectId: string,
  data: UpdateProjectRequest
): Promise<ProjectResponse> => {
  try {
    const response = await axiosInstance.patch(`/projects/${projectId}`, data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao atualizar projeto'
      )
    );
  }
};

/*
  Delete a project
  Removes a project and its associated chats (soft delete)

  @async
  @param {string} projectId - UUID of the project to delete
  @returns {Promise<{success: boolean, message: string}>} Deletion confirmation
  @throws {Error} If deletion fails

  @example
  ```typescript
  try {
    const result = await deleteProject("550e8400-e29b-41d4-a716-446655440000");
    console.log('Deletion result:', result.message);
  } catch (error) {
    console.error('Failed to delete project:', error);
  }
  ```
 */
export const deleteProject = async (projectId: string): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await axiosInstance.delete(`/projects/${projectId}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao deletar projeto'
      )
    );
  }
};

/*
  Set a project as active
  Marks a specific project as the current active project for the user

  @async
  @param {string} projectId - UUID of the project to set as active
  @returns {Promise<ProjectResponse>} Updated project data
  @throws {Error} If operation fails

  @example
  ```typescript
  try {
    const activeProject = await setActiveProject("550e8400-e29b-41d4-a716-446655440000");
    console.log('Active project:', activeProject.data);
  } catch (error) {
    console.error('Failed to set active project:', error);
  }
  ```
 */
export const setActiveProject = async (projectId: string): Promise<ProjectResponse> => {
  try {
    const response = await axiosInstance.post(`/projects/${projectId}/set-active`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao definir projeto ativo'
      )
    );
  }
};

/*
  Get the current active project
  Retrieves the project that is currently marked as active for the user

  @async
  @returns {Promise<ProjectResponse>} Active project data
  @throws {Error} If active project cannot be fetched

  @example
  ```typescript
  try {
    const activeProject = await getActiveProject();
    console.log('Active project:', activeProject.data);
  } catch (error) {
    console.error('Failed to fetch active project:', error);
  }
  ```
 */
export const getActiveProject = async (): Promise<ProjectResponse> => {
  try {
    const response = await axiosInstance.get('/projects/active');
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar projeto ativo'
      )
    );
  }
};

/*
  Check if a project name is unique for the user
  Validates that the project name is not already in use

  @async
  @param {string} name - Project name to check
  @param {string} [excludeProjectId] - Optional project ID to exclude from check (for updates)
  @returns {Promise<{success: boolean, is_unique: boolean}>} Uniqueness check result
  @throws {Error} If validation fails

  @example
  ```typescript
  try {
    const result = await checkProjectNameUnique("My Project");
    if (!result.is_unique) {
      console.log('Project name already exists');
    }
  } catch (error) {
    console.error('Failed to check project name:', error);
  }
  ```
 */
export const checkProjectNameUnique = async (
  name: string,
  excludeProjectId?: string
): Promise<{
  success: boolean;
  is_unique: boolean;
}> => {
  try {
    const params: Record<string, string> = { name };
    if (excludeProjectId) {
      params.exclude_project_id = excludeProjectId;
    }

    const response = await axiosInstance.get('/projects/check-name', {
      params
    });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao verificar nome do projeto'
      )
    );
  }
};

export const uploadProjectDocuments = async (
  projectId: string,
  files: File[]
): Promise<{
  success: boolean;
  message: string;
  data: ProjectDocument[];
}> => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const response = await axiosInstance.post(
      `/projects/${projectId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao anexar documentos ao projeto'
      )
    );
  }
};

export const getProjectDocuments = async (
  projectId: string
): Promise<{
  success: boolean;
  message: string;
  data: ProjectDocument[];
}> => {
  try {
    const response = await axiosInstance.get(`/projects/${projectId}/documents`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar documentos do projeto'
      )
    );
  }
};

export const deleteProjectDocument = async (
  projectId: string,
  documentId: number
): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await axiosInstance.delete(
      `/projects/${projectId}/documents/${documentId}`
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao remover documento do projeto'
      )
    );
  }
};
